import { useEffect, useMemo, useState, type FormEvent } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { ArrowLeft } from 'lucide-react'
import { useApp } from '../AppContext'
import { useAsync } from '../hooks/useAsync'
import { hasCapability } from '../auth/roles'
import { TokenRevealModal } from '../components/TokenRevealModal'
import { ConfirmRotateModal } from '../components/ConfirmRotateModal'
import { ConfirmDeleteModal } from '../components/ConfirmDeleteModal'
import { CredentialPanel } from '../components/CredentialPanel'
import { EntitlementEditModal } from '../components/EntitlementEditModal'
import { IssueFlightdeckModal } from '../components/IssueFlightdeckModal'
import { SetExpiryModal } from '../components/SetExpiryModal'
import { Notice } from '../components/primitives/Notice'
import { refusalSentence } from '../theme/vocabulary'
import { ApiError } from '../api/client'
import { PageHeader } from '../components/primitives/PageHeader'
import { Panel } from '../components/primitives/Panel'
import { DataTable, type DataTableColumn } from '../components/primitives/DataTable'
import { Fact, FactList, Mono } from '../components/primitives/Fact'
import { Field } from '../components/primitives/Field'
import { EnvironmentStatusChip } from '../components/primitives/StatusChip'
import { migrationTypeLabel } from '../theme/vocabulary'
import type {
  AccountListResponse,
  EnvironmentDetail as Detail,
  Device,
  DeviceListResponse,
  EnvironmentCredential,
  EnvironmentStatus,
  Entitlement,
  PrincipalClass,
  TokenIssuanceResponse,
} from '../api/types'

const ALL_STATUSES: EnvironmentStatus[] = ['active', 'suspended', 'revoked']

// Drill-in for one environment: Licensing-owned fields + account/balance
// (ATC-051 aggregate) + MINT devices (ATC-052), plus the Phase 2 write surface
// (lifecycle, token rotation, account mapping) gated on the write capability.
//
// ATC-110 converted this to the light shell. The rotation flow's BEHAVIOUR is
// deliberately untouched: `FD-ADR-038` §1.11 promoted ATC's amber-trigger →
// red-confirm escalation into shared Tier 1 FROM ATC, naming this dialog as the
// standard the other two products must match, because it states the downstream
// break by name rather than asking "are you sure". Only its colours moved.
export function EnvironmentDetail() {
  const { cei = '' } = useParams()
  const navigate = useNavigate()
  const { api, role } = useApp()
  const canWrite = hasCapability(role, 'write')

  const detail = useAsync<Detail>(() => api.getEnvironment(cei), [cei])
  const devices = useAsync<DeviceListResponse>(() => api.listDevices(cei), [cei])
  // 200 is the server's page cap. At pilot scale this is every customer in one
  // request, and it is a LIST path, so it is one call for the page and never
  // one per row.
  const accounts = useAsync<AccountListResponse>(() => api.listAccounts({ pageSize: 200 }), [])

  // The free-text fallback exists ONLY when the picker genuinely cannot do the
  // job — the list failed, or the registry holds more customers than one page.
  // It is gated rather than always-available because a permanent escape hatch
  // would reintroduce exactly the typed-identifier failure the registry was
  // built to remove; a `<select>` past a few hundred entries is unusable
  // anyway, and the real answer there is a server-searched combobox.
  const usePicker = Boolean(accounts.data) && !accounts.data?.continuationToken

  // ATC-141. The same one call already backs the picker, so naming the mapped
  // account costs nothing extra.
  const mappedAccountId = detail.data?.account?.accountId ?? ''

  // Seeded from the loaded detail, keyed on the mapped id so it fires once per
  // mapping rather than on every render — an operator's half-made selection
  // must not be stomped by a re-render.
  useEffect(() => {
    if (mappedAccountId) setMapAccountId(mappedAccountId)
  }, [mappedAccountId])

  const accountNames = useMemo(() => {
    const map = new Map<string, string>()
    for (const a of accounts.data?.accounts ?? []) {
      if (a.accountName) map.set(a.accountId, a.accountName)
    }
    return map
  }, [accounts.data])

  const [busy, setBusy] = useState(false)
  const [actionError, setActionError] = useState<string | null>(null)
  const [revealed, setRevealed] = useState<TokenIssuanceResponse | null>(null)
  const [confirmingRotate, setConfirmingRotate] = useState<PrincipalClass | null>(null)
  const [editingEntitlements, setEditingEntitlements] = useState<EnvironmentCredential | null>(null)
  const [issuingFlightdeck, setIssuingFlightdeck] = useState(false)
  const [settingExpiry, setSettingExpiry] = useState(false)
  const [revealClass, setRevealClass] = useState<PrincipalClass>('device')
  const [now] = useState(() => Date.now())
  const [mapAccountId, setMapAccountId] = useState('')
  const [mapDone, setMapDone] = useState(false)
  const [entitlementNotice, setEntitlementNotice] = useState<string | null>(null)
  const [confirmingDelete, setConfirmingDelete] = useState(false)

  // ATC-142. ANY active credential blocks the delete, not just the one the
  // top-level `status` reports. An environment whose `atc_fd_` credential is
  // still active can still build a provisioning package carrying a tenant-join
  // credential, so "the device token was revoked" does not make it dead. Falls
  // back to the top-level status when the server has not sent per-credential
  // rows, which is the same degradation the rest of this page makes.
  const isActive = detail.data
    ? detail.data.credentials
      ? detail.data.credentials.some((c) => c.status === 'active')
      : detail.data.status === 'active'
    : false

  async function run(action: () => Promise<void>) {
    setBusy(true)
    setActionError(null)
    try {
      await action()
    } catch (err) {
      // Refusal codes become sentences (ATC-129). The server's own message is
      // the fallback, never blank.
      setActionError(
        err instanceof ApiError
          ? refusalSentence(err.code, err.message)
          : err instanceof Error
            ? err.message
            : String(err),
      )
    } finally {
      setBusy(false)
    }
  }

  // ATC-142. On success there is no page left to render -- the environment is
  // gone and `detail.reload()` would 404 -- so this navigates away rather than
  // reloading, and carries a one-line receipt in router state. The list renders
  // it, because this is the last moment anything in the console knows the
  // label: after this the only surviving record is the App Insights
  // `EnvironmentDeleted` event.
  function deleteEnvironment() {
    return run(async () => {
      const result = await api.deleteEnvironment(cei)
      setConfirmingDelete(false)
      navigate('/environments', {
        replace: true,
        state: {
          deleted: {
            environmentLabel: result.environmentLabel,
            credentialsDeleted: result.credentialsDeleted,
          },
        },
      })
    })
  }

  function changeStatus(status: EnvironmentStatus) {
    return run(async () => {
      await api.setEnvironmentStatus(cei, status)
      detail.reload()
    })
  }

  // Rotation is a destructive re-key (invalidates the current token), so it is
  // gated behind ConfirmRotateModal; only setConfirmingRotate opens it. On
  // success we close the confirm and reveal the new token (variant 'rotated');
  // on failure the confirm modal stays open and surfaces actionError inline.
  // ATC-131: the class comes from the credential panel the operator acted on,
  // so on a two-credential environment there is no default to get wrong.
  function rotate(principalClass: PrincipalClass) {
    return run(async () => {
      const result = await api.rotateEnvironmentToken(cei, principalClass)
      setConfirmingRotate(null)
      setRevealClass(principalClass)
      setRevealed(result)
      detail.reload()
    })
  }

  function saveEntitlements(principalClass: PrincipalClass, add: string[], remove: string[]) {
    return run(async () => {
      const result = await api.editEntitlements({
        customerEnvironmentId: cei,
        principalClass,
        add,
        remove,
      })
      setEditingEntitlements(null)
      // `changed: false` is a 200, not a failure — the requested state already
      // held. Reported as an ok notice rather than silence, so the operator
      // knows the request landed.
      setEntitlementNotice(result.changed ? 'Entitlements updated.' : 'Already set — no change.')
      detail.reload()
    })
  }

  function mintFlightdeck(entitledServices: Entitlement[]) {
    return run(async () => {
      try {
        const result = await api.issueFlightdeckCredential({
          customerEnvironmentId: cei,
          customerName: detail.data?.customerName ?? '',
          entitledServices,
        })
        setIssuingFlightdeck(false)
        setRevealClass('flightdeck')
        setRevealed(result)
      } catch (err) {
        // A 409 here means the local projection is STALE: the credential
        // already exists and this page does not know. Reload so the operator
        // sees it rather than re-reading a page that disagrees with the server.
        if (err instanceof ApiError && err.code === 'flightdeck_principal_already_issued') {
          detail.reload()
        }
        throw err
      }
      detail.reload()
    })
  }

  function saveExpiry(expiresAt: string | null) {
    return run(async () => {
      await api.setEnvironmentExpiry({ customerEnvironmentId: cei, expiresAt })
      setSettingExpiry(false)
      detail.reload()
    })
  }

  function submitMap(e: FormEvent) {
    e.preventDefault()
    return run(async () => {
      await api.mapAccountEnvironment({
        accountId: mapAccountId.trim(),
        customerEnvironmentId: cei,
      })
      setMapDone(true)
      detail.reload()
    })
  }

  const deviceColumns: DataTableColumn<Device>[] = [
    { key: 'serial', header: 'Serial', render: (d) => <Mono>{d.serialNumber}</Mono> },
    { key: 'migration', header: 'Migration', render: (d) => <Mono>{d.migrationId}</Mono> },
    {
      key: 'type',
      header: 'Type',
      // `Hybrid (D2C)` / `Cloud (T2T)` — BOTH forms, mapped at the render
      // boundary in exactly one module. §1.9 maps only the noun; ATC shows both
      // because the operator reading this is the same person who greps device
      // logs for `D2C`. The wire value is never altered.
      render: (d) => migrationTypeLabel(d.migrationType),
    },
    { key: 'apiBaseUrl', header: 'API base URL', render: (d) => <span className="url">{d.apiBaseUrl}</span> },
    { key: 'registered', header: 'Registered', render: (d) => <Mono>{d.registeredAt}</Mono> },
    {
      key: 'lastResolved',
      header: 'Last resolved',
      // Already a word rather than an em-dash before the conversion, so it is
      // left exactly as it was — the three sites ATC-110 changes are the ones
      // that rendered "—".
      render: (d) =>
        d.lastResolvedAt ? <Mono>{d.lastResolvedAt}</Mono> : <span className="muted">never</span>,
    },
  ]

  return (
    <section>
      <PageHeader
        back={
          <Link to="/environments" className="rx-page-back">
            <ArrowLeft size={14} strokeWidth={1.5} aria-hidden="true" /> Environments
          </Link>
        }
        title={detail.data ? detail.data.customerName || <em>(unnamed)</em> : 'Environment'}
        subtitle={detail.data ? <Mono>{detail.data.customerEnvironmentId}</Mono> : undefined}
      />

      {detail.error && <Notice tone="error">{detail.error}</Notice>}
      {detail.loading && <Notice>Loading…</Notice>}

      {detail.data && (
        <>
          {actionError && <Notice tone="error">{actionError}</Notice>}
          {entitlementNotice && <Notice tone="ok">{entitlementNotice}</Notice>}

          <div className="cards">
            <Panel title="Environment">
              <FactList>
                <Fact label="Status">
                  <EnvironmentStatusChip status={detail.data.status} />
                </Fact>
                <Fact label="Entitlements">{detail.data.entitledServices.join(', ')}</Fact>
                <Fact label="Issued">
                  <Mono>{detail.data.issuedAt}</Mono>
                </Fact>
                <Fact label="Expires">
                  {detail.data.expiresAt ? <Mono>{detail.data.expiresAt}</Mono> : 'never'}
                </Fact>
                <Fact label="Customer">
                  {detail.data.account?.accountId ? (
                    // ATC-130: links to the customer. The <=760px breakpoint
                    // hides the nav entirely, so Customers must be reachable
                    // without it.
                    //
                    // ATC-141: the NAME, falling back to the id. A GUID here
                    // was the only thing on the page identifying the customer,
                    // and it is the one fact an operator opens this page to
                    // check.
                    <Link to={`/customers/${detail.data.account.accountId}`}>
                      {accountNames.get(detail.data.account.accountId) ?? (
                        <Mono>{detail.data.account.accountId}</Mono>
                      )}
                    </Link>
                  ) : (
                    <span className="muted">Unmapped</span>
                  )}
                </Fact>
              </FactList>

              {canWrite && (
                <div className="actions">
                  {ALL_STATUSES.filter((s) => s !== detail.data!.status).map((s) => (
                    <button key={s} disabled={busy} onClick={() => changeStatus(s)}>
                      {s === 'active' ? 'Reactivate' : s === 'suspended' ? 'Suspend' : 'Revoke'}
                    </button>
                  ))}
                  {/* The AMBER TRIGGER of the §1.11 escalation. Its red confirm
                      lives in ConfirmRotateModal; neither the two-step flow nor
                      the acknowledgement checkbox is changed here. */}
                  {/* Only when the server has not sent per-credential rows:
                      once it does, rotation is a per-credential action on the
                      panel and this would be a second, ambiguous entry point. */}
                  {!detail.data.credentials && (
                    <button
                      className="btn-warn"
                      disabled={busy}
                      onClick={() => {
                        setActionError(null)
                        setConfirmingRotate('device')
                      }}
                    >
                      Rotate token…
                    </button>
                  )}
                  <button
                    disabled={busy}
                    onClick={() => {
                      setActionError(null)
                      setSettingExpiry(true)
                    }}
                  >
                    Expiry…
                  </button>
                </div>
              )}
            </Panel>

            {/* CREDENTIALS. `credentials` is OPTIONAL on the type, so a console
                deployed ahead of the services degrades to the single-credential
                view above rather than rendering `undefined.map`. Console and
                services deploy in one workflow run but from independent PRs, so
                a console-first merge is a real state, not a hypothetical. */}
            {detail.data.credentials && (
              <div className="stack">
                {detail.data.credentials.map((credential) => (
                  <CredentialPanel
                    key={credential.principalClass}
                    credential={credential}
                    now={now}
                    actions={
                      canWrite ? (
                        <div className="rx-page-header-actions">
                          <button
                            disabled={busy}
                            onClick={() => {
                              setActionError(null)
                              setEntitlementNotice(null)
                              setEditingEntitlements(credential)
                            }}
                          >
                            Entitlements…
                          </button>
                          {/* The AMBER TRIGGER of the §1.11 escalation, now
                              per credential — "rotate the token" is ambiguous
                              on an environment holding both, and the two have
                              completely different downstream consumers. */}
                          <button
                            className="btn-warn"
                            disabled={busy}
                            onClick={() => {
                              setActionError(null)
                              setConfirmingRotate(credential.principalClass)
                            }}
                          >
                            Rotate…
                          </button>
                        </div>
                      ) : undefined
                    }
                  />
                ))}

                {/* "No value" is rendered as an ABSENT STATE IN WORDS with the
                    action beside it (§1.3), never an em-dash. */}
                {!detail.data.credentials.some((c) => c.principalClass === 'flightdeck') && (
                  <Panel title="Flight Deck credential">
                    <p className="muted">
                      No Flight Deck credential. This environment cannot build provisioning
                      packages — the ppkg endpoints require the <code>atc_fd_</code> class.
                    </p>
                    {canWrite && (
                      <div className="actions">
                        <button
                          disabled={busy}
                          onClick={() => {
                            setActionError(null)
                            setIssuingFlightdeck(true)
                          }}
                        >
                          Issue Flight Deck credential…
                        </button>
                      </div>
                    )}
                  </Panel>
                )}
              </div>
            )}

            <Panel title="Migration licenses">
              {detail.data.balance ? (
                <FactList>
                  <Fact label="Consumable available">
                    <Mono>
                      {detail.data.balance.consumable.available} / {detail.data.balance.consumable.granted}
                    </Mono>
                  </Fact>
                  <Fact label="Consumable burned">
                    <Mono>{detail.data.balance.consumable.burned}</Mono>
                  </Fact>
                  <Fact label="Pool available">
                    <Mono>
                      {detail.data.balance.pool.available} / {detail.data.balance.pool.capacity}
                    </Mono>
                  </Fact>
                  <Fact label="Pool in use">
                    <Mono>{detail.data.balance.pool.inUse}</Mono>
                  </Fact>
                  <Fact label="Reclaimed (open)">
                    <Mono>{detail.data.balance.reclaimedOpen}</Mono>
                  </Fact>
                </FactList>
              ) : (
                // Prose, not a cell value — the em-dash here is punctuation and
                // is deliberately left alone. A regex sweep over em-dashes
                // damages copy like this.
                <p className="muted">No account mapped — no migration-license balance.</p>
              )}

              {canWrite && (
                // ATC-138. A PICKER, not two free-text fields.
                //
                // `ATC-ADR-013`'s R-ATC-10 amendment says plainly that "the
                // console never free-texts an accountId again" — an account
                // used to be created BY BEING MENTIONED, so a typo silently
                // produced a second, invisible account and mapped into it.
                // ATC-130 honoured that on the customers page; this form was
                // never converted, so the operator was still pasting a GUID
                // after Sprint 9 shipped.
                //
                // `accountName` is deliberately NOT sent. It is the
                // PRE-registry copy of the name, and since
                // Set-AtcAccountRegistryTouch never overwrites a non-blank
                // registry name, sending a different one here produced two
                // permanent names for one customer. The registry owns the name;
                // rename it on the customer page.
                <form className="inline-form" onSubmit={submitMap}>
                  {usePicker ? (
                    // ATC-141. The picker rendered blank on an environment that
                    // was ALREADY MAPPED, which reads as "no customer" directly
                    // beside a Customer fact naming one. It is preselected, and
                    // the label says which of the two actions this is.
                    <Field label={mappedAccountId ? 'Change customer' : 'Map to customer'}>
                      <select
                        value={mapAccountId}
                        onChange={(e) => {
                          setMapAccountId(e.target.value)
                          setMapDone(false)
                        }}
                      >
                        <option value="">Select a customer…</option>
                        {(accounts.data?.accounts ?? []).map((a) => (
                          <option key={a.accountId} value={a.accountId}>
                            {a.accountName || a.accountId}
                          </option>
                        ))}
                      </select>
                    </Field>
                  ) : (
                    <Field label="Map to customer (account ID)">
                      <input
                        type="text"
                        placeholder="Account ID"
                        value={mapAccountId}
                        onChange={(e) => {
                          setMapAccountId(e.target.value)
                          setMapDone(false)
                        }}
                      />
                    </Field>
                  )}
                  <div className="row">
                    <button
                      type="submit"
                      disabled={busy || !mapAccountId.trim() || mapAccountId === mappedAccountId}
                    >
                      {mappedAccountId ? 'Change' : 'Map'}
                    </button>
                    <Link to="/customers">Manage customers</Link>
                  </div>
                  {!usePicker && !accounts.loading && (
                    <Notice tone="warn">
                      {accounts.error
                        ? 'The customer list could not be loaded, so this falls back to an account ID. Check the ID on the Customers page before mapping.'
                        : 'More customers exist than this picker lists. Copy the account ID from the Customers page.'}
                    </Notice>
                  )}
                  {mapDone && <Notice tone="ok">Mapped.</Notice>}
                </form>
              )}
            </Panel>
          </div>
        </>
      )}

      {devices.error && <Notice tone="error">{devices.error}</Notice>}

      {/* `flush` because the table draws its own row rules right to the panel
          stroke. This is the second of ATC's two densest tables, and the panel
          is what puts its rows on white. */}
      <Panel title="Devices" flush>
        <DataTable
          caption="Devices registered in this environment"
          columns={deviceColumns}
          data={devices.data?.devices ?? []}
          keyForRow={(d) => d.serialNumber}
          isLoading={devices.loading}
          error={devices.error ? 'Could not load devices.' : undefined}
          emptyState="No devices registered for this environment."
        />
      </Panel>

      {/* ATC-142. A DANGER ZONE, placed last on purpose: the operator reaches it
          only after scrolling past the credentials and the device list -- the
          two facts that decide whether this is the right environment. Putting
          Delete beside Suspend and Revoke in the Environment panel would sit an
          unrecoverable action in a row of reversible ones, a keystroke apart. */}
      {canWrite && detail.data && (
        <Panel title="Delete environment">
          <p className="muted">
            Permanently destroys this environment and every credential it holds. Migration-license
            grants belong to the customer and are not affected.
          </p>
          <div className="actions">
            {/* The AMBER TRIGGER of the §1.11 escalation; its red confirm lives
                in ConfirmDeleteModal. Disabled rather than hidden while the
                environment is active, because a missing button teaches nothing
                -- the server refuses this too (409 `environment_active`), and
                the reason is the point. */}
            <button
              className="btn-warn"
              disabled={busy || isActive}
              onClick={() => {
                setActionError(null)
                setConfirmingDelete(true)
              }}
            >
              Delete environment…
            </button>
            {isActive && (
              <span className="muted">
                Suspend or revoke it first — deleting must never be the first thing that stops a
                live fleet, and revocation is reversible where this is not.
              </span>
            )}
          </div>
        </Panel>
      )}

      {confirmingRotate && detail.data && (
        <ConfirmRotateModal
          customerEnvironmentId={detail.data.customerEnvironmentId}
          customerName={detail.data.customerName}
          principalClass={confirmingRotate}
          busy={busy}
          error={actionError}
          onConfirm={() => rotate(confirmingRotate)}
          onCancel={() => {
            setConfirmingRotate(null)
            setActionError(null)
          }}
        />
      )}

      {editingEntitlements && (
        <EntitlementEditModal
          customerEnvironmentId={cei}
          principalClass={editingEntitlements.principalClass}
          before={editingEntitlements.entitledServices}
          busy={busy}
          error={actionError}
          onConfirm={(add, remove) =>
            saveEntitlements(editingEntitlements.principalClass, add, remove)
          }
          onCancel={() => {
            setEditingEntitlements(null)
            setActionError(null)
          }}
        />
      )}

      {issuingFlightdeck && detail.data && (
        <IssueFlightdeckModal
          customerEnvironmentId={cei}
          customerName={detail.data.customerName}
          busy={busy}
          error={actionError}
          onConfirm={mintFlightdeck}
          onCancel={() => {
            setIssuingFlightdeck(false)
            setActionError(null)
          }}
        />
      )}

      {settingExpiry && detail.data && (
        <SetExpiryModal
          customerEnvironmentId={cei}
          currentExpiresAt={detail.data.expiresAt}
          busy={busy}
          error={actionError}
          onConfirm={saveExpiry}
          onCancel={() => {
            setSettingExpiry(false)
            setActionError(null)
          }}
        />
      )}

      {confirmingDelete && detail.data && (
        <ConfirmDeleteModal
          customerEnvironmentId={detail.data.customerEnvironmentId}
          environmentLabel={detail.data.customerName}
          credentials={detail.data.credentials ?? []}
          /* `null` when the device list failed, and the modal says so rather
             than implying zero. The server structurally cannot check this --
             Licensing is a leaf and must not call MINT -- so this page's
             already-fetched count is the only place it is known. */
          deviceCount={devices.error ? null : (devices.data?.devices.length ?? null)}
          busy={busy}
          error={actionError}
          onConfirm={deleteEnvironment}
          onCancel={() => {
            setConfirmingDelete(false)
            setActionError(null)
          }}
        />
      )}

      {revealed && (
        <TokenRevealModal
          result={revealed}
          variant={revealClass === 'flightdeck' ? 'issued' : 'rotated'}
          principalClass={revealClass}
          onClose={() => setRevealed(null)}
        />
      )}
    </section>
  )
}
